/**
 * Hive Spaces Client - API client for managing collaborative Hive Spaces.
 */

import type { UIMessage } from "ai";

import { getAuthTokenAsync } from "@/lib/auth";
import { getApiUrl } from "@/lib/api-config";

const API_BASE = getApiUrl();

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthTokenAsync();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HiveSpace {
  id: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  status: string;
  max_applications: number;
  max_concurrent_users: number;
  connected_dataspace_id: string | null;
  dataspace_metadata: Record<string, unknown>;
  cluster_ids: string[];
  extra_connection_ids: string[];
  doc_file_ids: string[];
  clusters?: ConnectionCluster[];
  doc_files?: HiveSpaceDocFile[];
  style_guide_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  member_role?: string;
  member_count?: number;
  motif_count?: number;
  shelf_count?: number;
  card_count?: number;
  creator_display_name?: string | null;
  creator_email?: string | null;
  members?: HiveSpaceMember[];
  motifs?: HiveSpaceMotif[];
}

export interface HiveSpaceDocFile {
  id: string;
  file_name: string;
  file_type: string;
  classification: string;
  industry: string;
  embedding_status: string;
  chunk_count: number;
  tags: string[];
}

export interface DocClusterFile {
  id: string;
  file_name: string;
  file_type: string;
  file_size_bytes: number;
  chunk_count: number;
  tags: string[];
  classification?: string | null;
  industry?: string | null;
}

export type DocClusterGroupBy = "industry" | "classification";

export interface DocCluster {
  group_by: DocClusterGroupBy;
  key: string;
  classification?: string | null;
  industry?: string | null;
  name: string;
  description: string;
  file_ids: string[];
  files: DocClusterFile[];
}

export interface HiveSpaceMember {
  id: string;
  hive_space_id: string;
  user_id: string;
  role: "admin" | "creator" | "viewer";
  status: string;
  joined_at: string;
  last_active_at: string;
  email?: string | null;
  display_name?: string | null;
  avatar_url?: string | null;
}

export interface HiveSpaceMotif {
  id: string;
  hive_space_id: string;
  name: string;
  description: string | null;
  slot: number;
  status: string;
  sandbox_id: string | null;
  app_state: Record<string, unknown>;
  motif_template_id: string | null;
  installed_from: "local" | "mstrohub";
  created_by: string;
  created_at: string;
}

export interface SpaceKit {
  id: string;
  app_id: string;
  hive_space_id: string;
  spacekit_type: string;
  source: "builtin" | "marketplace";
  status: string;
  config: Record<string, unknown>;
  connection_info: Record<string, unknown>;
  sandbox_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface SpaceKitRegistryItem {
  type: string;
  name: string;
  description: string;
  version: string;
  default_port: number;
  default_config: Record<string, unknown>;
}

export interface HiveUser {
  id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  role: string;
  hub_role: string | null;
}

export interface HiveSpaceMessage {
  id: string;
  hive_space_id: string;
  user_id: string;
  content: string;
  role: string;
  created_at: string;
}

export interface ChannelStatus {
  channelId: string;
  status: string;
  dataSpaceId?: string;
  motifId?: string;
}

// ---------------------------------------------------------------------------
// Tool Artifacts (Docs + Sheets)
// ---------------------------------------------------------------------------

export type ToolArtifactType = "doc" | "sheet";

export type DocArtifactContent =
  | { format: "markdown"; markdown: string; last_writer_prompt?: string }
  | {
      format: "html";
      html: string;
      title?: string;
      charts?: Array<Record<string, unknown>>;
      last_writer_prompt?: string;
    };

export function getDocFormat(content: DocArtifactContent | undefined | null): "markdown" | "html" {
  if (!content) return "html";
  return content.format === "html" ? "html" : "markdown";
}

export function getDocMarkdown(content: DocArtifactContent | undefined | null): string {
  if (!content) return "";
  return content.format === "markdown" ? content.markdown : "";
}

export function getDocHtml(content: DocArtifactContent | undefined | null): string {
  if (!content) return "";
  return content.format === "html" ? content.html : "";
}

export interface SheetArtifactColumn {
  key: string;
  name: string;
}

export type SheetCellValue = string | number | null;

export interface SheetArtifactContent {
  columns: SheetArtifactColumn[];
  rows: Array<Record<string, SheetCellValue>>;
}

export type RecreationInterval = "off" | "daily" | "weekly" | "monthly";
export type ToolChatMessage = UIMessage;

export interface ToolArtifact<
  T extends ToolArtifactType = ToolArtifactType,
  C = T extends "doc" ? DocArtifactContent : SheetArtifactContent,
> {
  id: string;
  hive_space_id: string;
  tool_type: T;
  title: string;
  content: C;
  chat_messages: ToolChatMessage[];
  recreation_interval: RecreationInterval;
  recreation_next_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export type DocArtifact = ToolArtifact<"doc", DocArtifactContent>;
export type SheetArtifact = ToolArtifact<"sheet", SheetArtifactContent>;

export interface ChannelVerdict {
  verdict: "approve" | "warn" | "reject";
  quality_score?: number;
  summary?: string;
  alternative_count?: number;
  remap_hint?: string;
}

export type ChannelVerdictBand =
  | "strong_pass"
  | "borderline"
  | "hard_fail"
  | "insufficient_sample";

export interface ChannelVerdictFull {
  verdict: "approve" | "warn" | "reject";
  quality_score: number;
  summary: string;
  gate_band?: ChannelVerdictBand | string;
  gate_score?: number;
  per_collection?: Array<{
    collection_name: string;
    quality: "good" | "acceptable" | "poor";
    field_coverage: number;
    notes?: string;
  }>;
  alternative_endpoints?: Array<{
    connection_id: string;
    connection_name?: string;
    endpoint_path: string;
    method?: string;
    relevance?: string;
  }>;
  remap_hint?: string | null;
  issues?: Array<{
    collection: string;
    field: string;
    kind: string;
    detail: string;
    hard: boolean;
  }>;
}

export function readChannelVerdict(
  appState: Record<string, unknown>,
): ChannelVerdict | null {
  const raw = appState?.channel_verdict;
  if (
    raw &&
    typeof raw === "object" &&
    "verdict" in (raw as Record<string, unknown>)
  ) {
    return raw as ChannelVerdict;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Spaces
// ---------------------------------------------------------------------------

export async function listSpaces(): Promise<HiveSpace[]> {
  return request("/v1/hive-spaces/");
}

export async function getSpace(id: string): Promise<HiveSpace> {
  return request(`/v1/hive-spaces/${id}`);
}

export async function createSpace(data: {
  name: string;
  description?: string;
  icon_url?: string;
  connected_dataspace_id?: string;
  cluster_ids?: string[];
  extra_connection_ids?: string[];
  doc_file_ids?: string[];
}): Promise<HiveSpace> {
  return request("/v1/hive-spaces/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateSpace(
  id: string,
  data: Partial<Pick<HiveSpace, "name" | "description" | "icon_url" | "status" | "connected_dataspace_id" | "dataspace_metadata" | "cluster_ids" | "extra_connection_ids" | "doc_file_ids" | "style_guide_id">>,
): Promise<HiveSpace> {
  return request(`/v1/hive-spaces/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteSpace(id: string): Promise<{ deleted: boolean }> {
  return request(`/v1/hive-spaces/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Clusters
// ---------------------------------------------------------------------------

export interface ClusterConnection {
  id: string;
  name: string;
  type: string;
}

export interface SuggestedCluster {
  category: string;
  name: string;
  description: string;
  connection_ids: string[];
  connections: ClusterConnection[];
}

export interface ConnectionCluster {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  connection_ids: string[];
  connections?: ClusterConnection[];
  auto_detected: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export async function suggestClusters(): Promise<SuggestedCluster[]> {
  const res = await request<{ clusters: SuggestedCluster[] }>("/v1/clusters/suggest");
  return res.clusters;
}

export async function listClusters(): Promise<ConnectionCluster[]> {
  const res = await request<{ clusters: ConnectionCluster[] }>("/v1/clusters");
  return res.clusters;
}

export async function createCluster(data: {
  name: string;
  connection_ids: string[];
  category?: string;
  description?: string;
}): Promise<ConnectionCluster> {
  return request("/v1/clusters", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Doc Clusters (files grouped by classification)
// ---------------------------------------------------------------------------

export async function listDocClusters(
  groupBy: DocClusterGroupBy = "industry",
): Promise<DocCluster[]> {
  const params = new URLSearchParams({ group_by: groupBy });
  return request<DocCluster[]>(`/v1/doc-serve/clusters?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

export async function expandSlots(
  spaceId: string,
  maxApplications: number,
): Promise<HiveSpace> {
  return request(`/v1/hive-spaces/${spaceId}/slots`, {
    method: "PATCH",
    body: JSON.stringify({ max_applications: maxApplications }),
  });
}

// ---------------------------------------------------------------------------
// Tool Artifacts (Docs + Sheets)
// ---------------------------------------------------------------------------

export async function listToolArtifacts(
  spaceId: string,
  toolType?: ToolArtifactType,
): Promise<ToolArtifact[]> {
  const qs = toolType ? `?tool_type=${toolType}` : "";
  return request(`/v1/hive-spaces/${spaceId}/tools${qs}`);
}

export async function createToolArtifact<T extends ToolArtifactType>(
  spaceId: string,
  data: {
    tool_type: T;
    title?: string;
    content?: T extends "doc" ? DocArtifactContent : SheetArtifactContent;
    chat_messages?: ToolChatMessage[];
  },
): Promise<ToolArtifact<T>> {
  return request(`/v1/hive-spaces/${spaceId}/tools`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function getToolArtifact<T extends ToolArtifactType = ToolArtifactType>(
  spaceId: string,
  artifactId: string,
): Promise<ToolArtifact<T>> {
  return request(`/v1/hive-spaces/${spaceId}/tools/${artifactId}`);
}

export async function updateToolArtifact<T extends ToolArtifactType = ToolArtifactType>(
  spaceId: string,
  artifactId: string,
  data: {
    title?: string;
    content?: T extends "doc" ? DocArtifactContent : SheetArtifactContent;
    chat_messages?: ToolChatMessage[];
    recreation_interval?: RecreationInterval;
  },
): Promise<ToolArtifact<T>> {
  return request(`/v1/hive-spaces/${spaceId}/tools/${artifactId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteToolArtifact(
  spaceId: string,
  artifactId: string,
): Promise<{ deleted: boolean }> {
  return request(`/v1/hive-spaces/${spaceId}/tools/${artifactId}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Doc Templates — re-usable HTML reports
// ---------------------------------------------------------------------------

export interface DocTemplate {
  id: string;
  title: string;
  html: string;
  preview_text: string | null;
  source_artifact_id: string | null;
  built_in: boolean;
  created_by: string | null;
  created_at: string;
}

export async function listDocTemplates(): Promise<DocTemplate[]> {
  return request(`/v1/doc-templates`);
}

export async function getDocTemplate(id: string): Promise<DocTemplate> {
  return request(`/v1/doc-templates/${id}`);
}

export async function createDocTemplate(data: {
  title: string;
  html: string;
  preview_text?: string;
  source_artifact_id?: string;
}): Promise<DocTemplate> {
  return request(`/v1/doc-templates`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deleteDocTemplate(id: string): Promise<{ deleted: boolean }> {
  return request(`/v1/doc-templates/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export async function getMembers(spaceId: string): Promise<HiveSpaceMember[]> {
  return request(`/v1/hive-spaces/${spaceId}/members`);
}

export async function getAvailableUsers(
  spaceId: string,
  search?: string,
): Promise<HiveUser[]> {
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  return request(`/v1/hive-spaces/${spaceId}/available-users${qs}`);
}

export async function addMember(
  spaceId: string,
  data: {
    user_id: string;
    role?: string;
    share_all_shelves?: boolean;
    shelf_ids?: string[];
  },
): Promise<HiveSpaceMember> {
  return request(`/v1/hive-spaces/${spaceId}/members`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function removeMember(
  spaceId: string,
  userId: string,
): Promise<{ removed: boolean }> {
  return request(`/v1/hive-spaces/${spaceId}/members/${userId}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export async function getMessages(
  spaceId: string,
  limit = 50,
  offset = 0,
): Promise<HiveSpaceMessage[]> {
  return request(`/v1/hive-spaces/${spaceId}/messages?limit=${limit}&offset=${offset}`);
}

export async function sendMessage(
  spaceId: string,
  content: string,
  role = "user",
): Promise<HiveSpaceMessage> {
  return request(`/v1/hive-spaces/${spaceId}/messages`, {
    method: "POST",
    body: JSON.stringify({ content, role }),
  });
}

// ---------------------------------------------------------------------------
// Local Templates (dev mode)
// ---------------------------------------------------------------------------

export interface LocalTemplate {
  slug: string;
  name: string;
  description: string | null;
  version: string;
  renderer: string;
  source: string;
}

export async function getLocalTemplates(spaceId: string): Promise<LocalTemplate[]> {
  return request(`/v1/hive-spaces/${spaceId}/local-templates`);
}

export async function installLocalTemplate(
  spaceId: string,
  data: { slug: string; slot: number; name?: string; description?: string },
): Promise<HiveSpaceMotif> {
  return request(`/v1/hive-spaces/${spaceId}/motifs/install-local`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Admin — Local Motif Template Management
// ---------------------------------------------------------------------------

export interface LocalTemplateDetail extends LocalTemplate {
  path?: string;
  build_md_content: string;
}

export async function adminListLocalTemplates(): Promise<LocalTemplate[]> {
  return request("/v1/admin/local-motif-templates");
}

export async function adminGetLocalTemplate(slug: string): Promise<LocalTemplateDetail> {
  return request(`/v1/admin/local-motif-templates/${slug}`);
}

export async function adminUpdateLocalTemplate(
  slug: string,
  data: { build_md_content: string },
): Promise<LocalTemplateDetail> {
  return request(`/v1/admin/local-motif-templates/${slug}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function adminDeleteLocalTemplate(slug: string): Promise<{ deleted: boolean; slug: string }> {
  return request(`/v1/admin/local-motif-templates/${slug}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Hub Motifs (mstroHub motif library)
// ---------------------------------------------------------------------------

export interface HubMotif {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon_url: string | null;
  thumbnail_url: string | null;
  stack_type: string;
  current_version: string | null;
  price_type: string;
  author: string | null;
  tags: string[];
}

export async function getHubMotifs(spaceId: string): Promise<HubMotif[]> {
  return request<HubMotif[]>(`/v1/hive-spaces/${spaceId}/hub-apps`);
}

export async function installHubMotif(
  spaceId: string,
  data: {
    motif_template_id: string;
    slot: number;
    name?: string;
    description?: string | null;
    version?: string | null;
    icon_url?: string | null;
    thumbnail_url?: string | null;
  },
): Promise<HiveSpaceMotif> {
  return request(`/v1/hive-spaces/${spaceId}/motifs/install`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Motifs
// ---------------------------------------------------------------------------

export async function listMotifs(spaceId: string): Promise<HiveSpaceMotif[]> {
  return request(`/v1/hive-spaces/${spaceId}/motifs`);
}

export async function deleteMotif(
  spaceId: string,
  motifId: string,
): Promise<{ deleted: boolean }> {
  return request(`/v1/hive-spaces/${spaceId}/motifs/${motifId}`, {
    method: "DELETE",
  });
}

export async function launchMotif(
  spaceId: string,
  data: {
    name: string;
    description?: string;
    slot: number;
    user_request: string;
    motif_template_id?: string;
  },
): Promise<HiveSpaceMotif> {
  return request(`/v1/hive-spaces/${spaceId}/motifs/launch`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function buildNewMotif(
  spaceId: string,
  data: {
    name: string;
    description?: string;
    slot: number;
    user_request: string;
  },
): Promise<HiveSpaceMotif & { build_md_slug: string; build_md_path: string }> {
  return request(`/v1/hive-spaces/${spaceId}/motifs/build-new`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

export async function initChannel(
  hiveSpaceId: string,
  appId?: string,
): Promise<{ channelId: string; dataSpaceId: string }> {
  return request("/v1/channels/init", {
    method: "POST",
    body: JSON.stringify({ hive_space_id: hiveSpaceId, app_id: appId }),
  });
}

export async function getChannelStatus(channelId: string): Promise<ChannelStatus> {
  return request(`/v1/channels/${channelId}/status`);
}

export async function refreshChannelData(channelId: string): Promise<{ status: string; channelId: string }> {
  return request(`/v1/channels/${channelId}/fetch`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// ---------------------------------------------------------------------------
// Motif SQLite Snapshots
// ---------------------------------------------------------------------------

export interface MotifSqliteStatus {
  has_snapshot?: boolean;
  id?: string;
  channel_id?: string | null;
  file_size_bytes?: number;
  content_sha256?: string;
  sqlite_user_version?: number;
  created_at?: string;
}

export async function getMotifSqliteSnapshot(
  hiveSpaceId: string,
  motifId: string,
  channelId?: string | null,
): Promise<ArrayBuffer | null> {
  const token = await getAuthTokenAsync();
  const params = new URLSearchParams();
  if (channelId) params.set("channel_id", channelId);
  const qs = params.toString();
  const res = await fetch(
    `${API_BASE}/v1/hive-spaces/${hiveSpaceId}/motifs/${motifId}/sqlite/latest${qs ? `?${qs}` : ""}`,
    {
      credentials: "include",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.arrayBuffer();
}

export async function uploadMotifSqliteSnapshot(
  hiveSpaceId: string,
  motifId: string,
  bytes: ArrayBuffer | Uint8Array,
  options: { channelId?: string | null; sqliteUserVersion?: number } = {},
): Promise<MotifSqliteStatus> {
  const token = await getAuthTokenAsync();
  const params = new URLSearchParams();
  if (options.channelId) params.set("channel_id", options.channelId);
  if (options.sqliteUserVersion != null) {
    params.set("sqlite_user_version", String(options.sqliteUserVersion));
  }
  const bodyBytes = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const uploadBytes = new Uint8Array(bodyBytes.byteLength);
  uploadBytes.set(bodyBytes);
  const res = await fetch(
    `${API_BASE}/v1/hive-spaces/${hiveSpaceId}/motifs/${motifId}/sqlite/snapshots?${params.toString()}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-sqlite3",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: new Blob([uploadBytes.buffer], { type: "application/x-sqlite3" }),
    },
  );
  if (!res.ok) {
    const errorBody = await res.text();
    throw new Error(`${res.status}: ${errorBody}`);
  }
  return res.json();
}

export async function getMotifSqliteStatus(
  hiveSpaceId: string,
  motifId: string,
  channelId?: string | null,
): Promise<MotifSqliteStatus> {
  const params = new URLSearchParams();
  if (channelId) params.set("channel_id", channelId);
  const qs = params.toString();
  return request(
    `/v1/hive-spaces/${hiveSpaceId}/motifs/${motifId}/sqlite/status${qs ? `?${qs}` : ""}`,
  );
}

// ---------------------------------------------------------------------------
// Scatter Board snapshot manifest
// ---------------------------------------------------------------------------

export interface ScatterBoardManifestEntry {
  motif_id: string;
  name: string;
  slot: number;
  status: string;
  app_url?: string | null;
  channel_id?: string | null;
  has_snapshot: boolean;
  snapshot_id?: string;
  snapshot_url?: string;
  content_sha256?: string;
  file_size_bytes?: number;
  sqlite_user_version?: number;
  updated_at?: string;
}

export interface ScatterBoardManifest {
  hive_space_id: string;
  motifs: ScatterBoardManifestEntry[];
}

export async function getScatterBoardManifest(
  hiveSpaceId: string,
): Promise<ScatterBoardManifest> {
  return request(`/v1/hive-spaces/${hiveSpaceId}/scatter-board/snapshots`);
}

export async function fetchSnapshotBytes(snapshotUrl: string): Promise<ArrayBuffer | null> {
  const token = await getAuthTokenAsync();
  const res = await fetch(`${API_BASE}${snapshotUrl}`, {
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.arrayBuffer();
}

// ---------------------------------------------------------------------------
// Per-space master SQLite snapshot — the consolidated, mapper-driven DB the
// Scatter Board (and other ESM apps) read from. Server-built; this is just
// the typed client for fetch + meta + rebuild.
// ---------------------------------------------------------------------------

export interface SpaceMasterCollectionSummary {
  collection_name: string;
  table: string;
  connection_id: string;
  connection_name: string;
  source_key: string;
  endpoint_paths?: string[];
  rows: number;
  row_cap?: number;
  cap_reached?: boolean;
}

export interface SpaceMasterConnectionSummary {
  connection_id: string;
  connection_name: string;
  connection_type?: string;
  rows: number;
  tables_at_cap: number;
}

export interface SpaceMasterMeta {
  has_snapshot: boolean;
  space_id: string;
  snapshot_version?: number;
  built_at?: string;
  size_bytes?: number | null;
  etag?: string | null;
  collections?: SpaceMasterCollectionSummary[];
  connections?: SpaceMasterConnectionSummary[];
  max_rows_per_table?: number;
  has_mapper_snapshot?: boolean;
  target_version?: number;
  sha256?: string;
}

export async function getSpaceMasterMeta(
  hiveSpaceId: string,
): Promise<SpaceMasterMeta> {
  return request(`/v1/hive-spaces/${hiveSpaceId}/sqlite/master/meta`);
}

export async function fetchSpaceMasterBytes(
  hiveSpaceId: string,
): Promise<ArrayBuffer | null> {
  const token = await getAuthTokenAsync();
  const res = await fetch(`${API_BASE}/v1/hive-spaces/${hiveSpaceId}/sqlite/master`, {
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status}: ${body}`);
  }
  return res.arrayBuffer();
}

export async function rebuildSpaceMaster(
  hiveSpaceId: string,
): Promise<SpaceMasterMeta & { rebuilt: boolean }> {
  return request(`/v1/hive-spaces/${hiveSpaceId}/sqlite/master/rebuild`, {
    method: "POST",
  });
}

// User edits made through the SpaceBase Master viewer are persisted server-side
// and replayed by the builder on every rebuild, so alterations stick across
// connection re-samples.
export interface SpaceMasterMutationInput {
  action: "insert" | "update" | "delete";
  table_name: string;
  collection_name?: string | null;
  record_id?: string | null;
  patch?: Record<string, unknown>;
  key?: Record<string, unknown> | null;
}

export interface SpaceMasterMutationRecord {
  id: string;
  hive_space_id: string;
  user_id: string;
  action: "insert" | "update" | "delete";
  table_name: string;
  collection_name: string | null;
  record_id: string | null;
  patch_json: Record<string, unknown>;
  key_json: Record<string, unknown> | null;
  created_at: string;
}

export async function recordSpaceMasterMutation(
  hiveSpaceId: string,
  mutation: SpaceMasterMutationInput,
): Promise<{ ok: boolean; mutation: SpaceMasterMutationRecord }> {
  return request(`/v1/hive-spaces/${hiveSpaceId}/sqlite/master/mutations`, {
    method: "POST",
    body: JSON.stringify(mutation),
  });
}

export async function listSpaceMasterMutations(
  hiveSpaceId: string,
): Promise<{ mutations: SpaceMasterMutationRecord[]; count: number }> {
  return request(`/v1/hive-spaces/${hiveSpaceId}/sqlite/master/mutations`);
}

export async function clearSpaceMasterMutations(
  hiveSpaceId: string,
): Promise<{ ok: boolean; removed: number }> {
  return request(`/v1/hive-spaces/${hiveSpaceId}/sqlite/master/mutations`, {
    method: "DELETE",
  });
}

export interface ScatterBoardBootstrapResult {
  hive_space_id: string;
  built: Array<{
    motif_id: string;
    snapshot_id: string;
    file_size_bytes: number;
    collections: string[];
  }>;
  skipped: Array<{ motif_id: string; reason: string; snapshot_id?: string }>;
  failures: Array<{ motif_id: string; error: string }>;
}

export async function bootstrapScatterBoardSnapshots(
  hiveSpaceId: string,
  options: { force?: boolean } = {},
): Promise<ScatterBoardBootstrapResult> {
  const params = new URLSearchParams();
  if (options.force) params.set("force", "true");
  const qs = params.toString();
  return request(
    `/v1/hive-spaces/${hiveSpaceId}/scatter-board/bootstrap${qs ? `?${qs}` : ""}`,
    { method: "POST" },
  );
}

export async function clearMotifSqliteSnapshots(
  hiveSpaceId: string,
  motifId: string,
  channelId?: string | null,
): Promise<{ cleared: number; cleared_at?: string; enabled?: boolean }> {
  const params = new URLSearchParams();
  if (channelId) params.set("channel_id", channelId);
  const qs = params.toString();
  return request(
    `/v1/hive-spaces/${hiveSpaceId}/motifs/${motifId}/sqlite/snapshots${qs ? `?${qs}` : ""}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// SpaceKits
// ---------------------------------------------------------------------------

export async function getSpaceKitRegistry(): Promise<SpaceKitRegistryItem[]> {
  return request("/v1/spacekits/registry");
}

export async function listSpaceKits(motifId: string): Promise<SpaceKit[]> {
  return request(`/v1/hive-spaces/motifs/${motifId}/spacekits`);
}

export async function activateSpaceKit(
  motifId: string,
  data: { spacekit_type: string; config?: Record<string, unknown> },
): Promise<SpaceKit> {
  return request(`/v1/hive-spaces/motifs/${motifId}/spacekits`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function deactivateSpaceKit(
  motifId: string,
  spaceKitType: string,
): Promise<{ deleted: boolean }> {
  return request(`/v1/hive-spaces/motifs/${motifId}/spacekits/${spaceKitType}`, {
    method: "DELETE",
  });
}

export async function getSpaceKitStatus(
  motifId: string,
  spaceKitType: string,
): Promise<SpaceKit> {
  return request(`/v1/hive-spaces/motifs/${motifId}/spacekits/${spaceKitType}/status`);
}

// ---------------------------------------------------------------------------
// Motif Rebuild
// ---------------------------------------------------------------------------

export async function rebuildMotif(
  spaceId: string,
  motifId: string,
  buildMdContent?: string,
): Promise<{ status: string; motif_id: string }> {
  return request(`/v1/hive-spaces/${spaceId}/motifs/${motifId}/rebuild`, {
    method: "POST",
    body: JSON.stringify(
      buildMdContent ? { build_md_content: buildMdContent } : {},
    ),
  });
}

// ---------------------------------------------------------------------------
// Motif Edit Harness — agentic loop driven by Inngest
// ---------------------------------------------------------------------------

export async function startMotifEditHarness(
  motifId: string,
  hiveSpaceId: string,
  userMessage: string,
): Promise<{ run_id: string; motif_id: string; hive_space_id: string; status: string }> {
  return request(`/v1/motifs/${motifId}/edit-harness/run`, {
    method: "POST",
    body: JSON.stringify({ user_message: userMessage, hive_space_id: hiveSpaceId }),
  });
}

export async function postMotifEditIframeResult(
  motifId: string,
  runId: string,
  result: { correlationId: string; success: boolean; error?: string },
): Promise<{ status: string; correlationId: string }> {
  return request(`/v1/motifs/${motifId}/edit-harness/${runId}/iframe-result`, {
    method: "POST",
    body: JSON.stringify(result),
  });
}

export function motifEditHarnessStreamUrl(motifId: string, runId: string): string {
  return `${API_BASE}/v1/motifs/${motifId}/edit-harness/${runId}/stream`;
}

// ---------------------------------------------------------------------------
// Motif Data Harness (graph-driven snapshot pipeline; replaces channels)
// ---------------------------------------------------------------------------

export interface MotifDataSnapshot {
  collections: Record<string, {
    data: unknown[];
    schema?: Record<string, unknown>;
    pagination?: { total?: number; offset?: number; limit?: number };
    sourceManifest?: Record<string, unknown>;
  }>;
  inventory_markdown?: string;
  source_index?: Array<Record<string, unknown>>;
  meta?: {
    started_at?: string;
    finished_at?: string;
    status?: string;
    source_counts?: { connections?: number; doc_files?: number; collections?: number };
    user_request?: string;
  };
}

export interface MotifDataResponse {
  motif_id: string;
  run_id?: string | null;
  status?: string;
  snapshot: MotifDataSnapshot | null;
  updated_at?: string;
  created_at?: string;
  error?: string | null;
}

export async function startMotifDataHarness(
  motifId: string,
  hiveSpaceId: string,
  userRequest?: string,
): Promise<{ run_id: string; motif_id: string; hive_space_id: string; status: string }> {
  return request(`/v1/motifs/${motifId}/data-harness/run`, {
    method: "POST",
    body: JSON.stringify({
      hive_space_id: hiveSpaceId,
      user_request: userRequest ?? "",
    }),
  });
}

export async function refreshMotifData(
  motifId: string,
  hiveSpaceId: string,
  userRequest?: string,
): Promise<{ run_id: string; motif_id: string; hive_space_id: string; status: string }> {
  return request(`/v1/motifs/${motifId}/data-harness/refresh`, {
    method: "POST",
    body: JSON.stringify({
      hive_space_id: hiveSpaceId,
      user_request: userRequest ?? "",
    }),
  });
}

export function motifDataHarnessStreamUrl(motifId: string, runId: string): string {
  return `${API_BASE}/v1/motifs/${motifId}/data-harness/${runId}/stream`;
}

export async function getMotifData(motifId: string): Promise<MotifDataResponse | null> {
  try {
    return await request(`/v1/motifs/${motifId}/data`);
  } catch (err) {
    // 404 = no snapshot yet (e.g. legacy motif). Return null instead of throwing
    // so callers can branch cleanly.
    if (err instanceof Error && /\b404\b/.test(err.message)) return null;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Save Motif Template (Live Edit)
// ---------------------------------------------------------------------------

export async function saveMotifTemplate(
  spaceId: string,
  motifId: string,
  data: { code: string },
): Promise<{ slug: string; path: string }> {
  return request(`/v1/hive-spaces/${spaceId}/motifs/${motifId}/save-template`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ---------------------------------------------------------------------------
// Channel Verdict & Remap
// ---------------------------------------------------------------------------

export async function getChannelVerdict(
  channelId: string,
): Promise<ChannelVerdictFull> {
  return request(`/v1/channels/${channelId}/verdict`);
}

export async function remapChannel(
  channelId: string,
  opts: {
    requirement_override?: string;
    preferred_endpoint_hint?: string;
    preferred_connection_id?: string;
    force_approve?: boolean;
  },
): Promise<{ status: string; channelId: string }> {
  return request(`/v1/channels/${channelId}/remap`, {
    method: "POST",
    body: JSON.stringify(opts),
  });
}

export async function getMotifStyleGuide(
  motifId: string,
): Promise<Record<string, unknown> | null> {
  return request(`/v1/hive-spaces/motifs/${motifId}/style-guide`);
}
